/**
 * The sentences a promo card says about a promotion, chosen from its stored
 * config. Kept out of the component for the same reason `claim-instructions`
 * is: what a card asserts about somebody's money is a rule, and a rule wants a
 * test, not a render.
 */
import type { TFunction } from 'i18next';
import { formatMoney } from '@/lib/money/index.js';
import { toBigOrNull as toBig, rebateRateConfig, wageringTargetMinor } from './reward-rules.js';
import {
  rebateClaimWindowDays,
  rebateClaimWindowEndsWithNextPeriod,
  rebatePeriodAnchor,
  rebatePeriodMode,
} from './rebate-period.js';
import type {
  CheckinEligibility,
  Promotion,
  RebateBlockedReason,
  RebatePreview,
} from './api.js';

export type RebateCaptionKey =
  | 'card.rebate.claimed'
  | 'card.rebate.otherClaimed'
  | 'card.rebate.accruing'
  | 'card.rebate.ratesChanged'
  | 'card.rebate.belowMinimum'
  | 'card.rebate.windowClosed'
  | 'card.rebate.none';

/**
 * English of last resort for every caption. An unresolved key renders as
 * itself, so without these a catalogue gap puts the literal string
 * `card.rebate.accruing` on a card — the same thing a player saw when a raw
 * error code reached a toast. The catalogue is the source of the copy; this is
 * only the floor under it.
 */
export const REBATE_CAPTION_FALLBACKS: Record<RebateCaptionKey, string> = {
  'card.rebate.claimed': 'Already claimed this period',
  'card.rebate.otherClaimed': 'Claimed on the other rebate this period',
  'card.rebate.accruing': 'Still adding up — this period has not closed yet',
  'card.rebate.ratesChanged': 'This promotion was updated — periods that ended before the change can no longer be claimed',
  'card.rebate.belowMinimum': 'This period is below the minimum you can claim',
  'card.rebate.windowClosed': 'The claim window for the last period has closed',
  'card.rebate.none': 'Nothing to claim yet',
};

/**
 * Every requirement still standing between this member and today's check-in.
 *
 * The server sends all of them, and all of them are shown: naming only the
 * first sends a member off to meet it and straight back into a second refusal
 * nobody mentioned.
 *
 * `required` is a digit string whose unit depends on the gate. The two money
 * gates carry MINOR UNITS and have to go through `formatMoney` with the brand's
 * currency — dividing by 100 here prints a JPY (0dp) threshold a hundred times
 * too small (#426). `minDepositCount` is a plain count and must never reach the
 * money formatter. A gate whose `required` is missing or not digits falls to
 * the "we could not check" line rather than rendering a number nobody sent.
 *
 * The shortfall is deliberately not computed. The member's own totals do travel
 * with the reply, but "you still need X" is a subtraction the server has not
 * made, and a stale progress figure under a live threshold reads as a promise.
 */
export function checkinGateLines(
  eligibility: CheckinEligibility | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
  money: (minor: bigint) => string,
): string[] {
  if (!eligibility || eligibility.eligible) return [];
  const unknown = t('card.daily_checkin.gate.unknown');
  const unmet = eligibility.unmet ?? [];
  if (unmet.length === 0) return [unknown];
  return unmet.map((u) => {
    const required = u.required;
    // `required` is a CURRENCY CODE here, not digits, so this is decided before
    // the digit test below sends every non-numeric value to the "try again"
    // line — which would tell a member to retry something that will never
    // change.
    if (u.gate === 'currency' && required) {
      return t('card.daily_checkin.gate.currency', { currency: required });
    }
    if (u.gate === 'unknown' || required == null || !/^\d+$/.test(required)) return unknown;
    if (u.gate === 'minDepositCount') {
      return t('card.daily_checkin.gate.minDepositCount', { times: required });
    }
    return t(`card.daily_checkin.gate.${u.gate}`, { amount: money(BigInt(required)) });
  });
}

/**
 * The sentence for each reason the server gives (#447).
 *
 * A lookup rather than a switch: a reason a newer svc-promo invents misses the
 * table and the caller falls back to the flag-based copy, which is the old
 * behaviour — a `switch` with no default would return undefined and the card
 * would say nothing at all about a period the member cannot claim.
 */
const CAPTION_BY_REASON: Record<RebateBlockedReason, RebateCaptionKey> = {
  claimed_by_sibling: 'card.rebate.otherClaimed',
  already_claimed: 'card.rebate.claimed',
  below_minimum: 'card.rebate.belowMinimum',
  rates_changed: 'card.rebate.ratesChanged',
  claim_window_closed: 'card.rebate.windowClosed',
  period_open: 'card.rebate.accruing',
  no_play: 'card.rebate.none',
};

/**
 * Caption under the period amount, or null when the amount speaks for itself.
 *
 * `blockedReason` is the server saying WHY it withheld the claim (#447). It is
 * what separates the two answers a member complains about, which the flags
 * cannot: a rebate confiscated by a rates edit and a period nobody has played
 * both arrive as `claimable: false` with a zero. It sits below the settled and
 * claimable checks because those two already have an unambiguous answer — a
 * settled period is settled whether or not it has closed, and a claimable
 * amount is captioned by the claim button, so it says nothing at all.
 * `periodClosed` and the closing "nothing to claim yet" stay as the fallback
 * for a preview from an svc-promo that predates the field.
 */
export function rebateCaptionKey(preview: RebatePreview | undefined): RebateCaptionKey | null {
  if (!preview) return null;
  if (preview.alreadyClaimed) {
    return preview.blockedBy ? 'card.rebate.otherClaimed' : 'card.rebate.claimed';
  }
  if (preview.claimable) return null;
  const byReason = preview.blockedReason ? CAPTION_BY_REASON[preview.blockedReason] : undefined;
  if (byReason) return byReason;
  if (preview.periodClosed === false) return 'card.rebate.accruing';
  return 'card.rebate.none';
}

/**
 * The figure the card puts under "this period".
 *
 * A settled period shows zero: nothing is left to take, and its gross accrual
 * reads as still-claimable at a glance. A period still running is the opposite
 * case — the figure is what has built up so far, and zeroing that says the
 * rebate was never calculated. Only the two together are a taken balance, so
 * only the two together are zeroed.
 */
export function rebateDisplayMinor(preview: RebatePreview | undefined): bigint {
  if (!preview) return 0n;
  if (preview.alreadyClaimed && preview.periodClosed !== false) return 0n;
  return toBig(preview.amountMinor) ?? 0n;
}

/**
 * The game types a per-type rebate rate can be keyed by, in the order a card
 * lists them. Mirrors `GAME_TYPES` in
 * `apps/web/backoffice/src/pages/promotion/game-types.ts` — the list the rebate
 * form offers and svc-bet's winloss rollup groups by. Listing in a fixed order
 * rather than the operator's row order keeps two promotions with the same rates
 * reading the same.
 */
const GAME_TYPE_ORDER = ['slot', 'fish', 'live', 'table', 'card', 'crash', 'other'];

/** A basis-point rate as a percentage without trailing zeros: 100 -> "1", 50 -> "0.5". */
function percentOf(bips: number): string {
  return String(bips / 100);
}

/**
 * `"<label> <pct>%"` per configured game type, or null when none is configured.
 *
 * A type set to zero is listed rather than dropped: `computeTurnoverRebate`
 * reads the per-type entry BEFORE falling back to the default, so a zero there
 * means that type earns nothing — the opposite of what the default-rate line
 * above it says. A negative entry reads as that same zero, because
 * `computeTurnoverRebate` skips every `bips <= 0` alike
 * (`apps/promo/src/repo/rebate-calc.ts:91-92`); printing "-0.5%" would state a
 * rate the engine pays in neither direction. A type with no label falls back to
 * its own name, because a rate the engine will apply must not go undisclosed
 * just because the catalogue has no word for it. The blank key the rebate
 * form's "+ row" writes before a type is picked is the one entry with nothing
 * to say.
 */
function gameTypeRates(bips: Record<string, number>, t: TFunction): string | null {
  const rank = (key: string) => {
    const i = GAME_TYPE_ORDER.indexOf(key);
    return i < 0 ? GAME_TYPE_ORDER.length : i;
  };
  const keys = Object.keys(bips)
    .filter((key) => key !== '')
    .sort((a, b) => rank(a) - rank(b));
  if (keys.length === 0) return null;
  return keys
    .map((key) => {
      const rate = Math.max(bips[key] ?? 0, 0);
      return `${t(`gameType.${key}`, { defaultValue: key })} ${percentOf(rate)}%`;
    })
    .join(' ');
}

/**
 * Whether any per-provider rate will supersede the rates the card just stated.
 *
 * `computeTurnoverRebate` rates a row `providerBips[providerId] ??
 * gameTypeBips[gameType] ?? defaultBips`, so ANY entry here outranks both lines
 * above it — a zero included, because `??` falls through on null/undefined only
 * and `bips <= 0` then pays that provider nothing. Only the blank key is not an
 * override: the rebate form's "+ row" writes `{ '': 0 }` until a provider is
 * picked, and no provider id is the empty string.
 */
function hasProviderOverrides(bips: Record<string, number>): boolean {
  return Object.keys(bips).some((key) => key !== '');
}

/**
 * The wagering multiplier a rebate line may state, or null when there is none.
 *
 * The `?? 5` default the other kinds use is deliberately absent, and the reason
 * survived the round trip through the reversal: an unset or zero multiplier means the
 * credit carries NO wagering requirement — `claimRebate` books it with
 * `turnoverRequired = 0` — so printing five would invent a lock the member does
 * not have. A positive value is now a real term the money path enforces, which
 * is why the line is back.
 */
function rebateMultiplier(raw: number | string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? String(n) : null;
}

/**
 * The expiry a rebate line may state, or null when the credit never expires.
 *
 * Same shape and the same rule: only a positive, configured number is a
 * deadline. It is the days the member has to FINISH the wagering — the expiry
 * sweeper forfeits the un-turned remainder — so a card that states it on a
 * credit with no requirement would be announcing a deadline for nothing, which
 * is why the caller only renders it beside a multiplier.
 */
function rebateExpiryDays(raw: number | string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? String(Math.trunc(n)) : null;
}

/**
 * Plain-language "how it works" lines per promo kind, with the real config
 * numbers (pct / cap / minDeposit / multiplier / day amounts) interpolated so
 * the copy always matches the promotion. A line whose source value is missing
 * is simply omitted.
 *
 * The two rebate kinds read every value through the same helpers the server
 * prices them with, defaults included, because a sentence about somebody's
 * money that the payout does not honour is the whole defect this file exists to
 * prevent. Two of them are CONDITIONAL rather than unconditional: the wagering
 * line and the expiry line appear only when the promotion actually set them,
 * because `claimRebate` writes `turnover_required = 0` and no `expires_at` for
 * a campaign that did not — and a card announcing a lock the member does
 * not have is the defect that ruling removed. The silence is now the zero case, not
 * every case.
 *
 * The expiry rides on the multiplier: what expires is the WAGERING window, so
 * on a credit that owes no wagering there is nothing for a deadline to be about.
 */
export function infoLines(
  promo: Promotion,
  currency: string,
  t: TFunction,
  formatLedger: (minor: bigint) => string = (m) => formatMoney(m, currency),
): string[] {
  const r = (promo.rewardRules ?? {}) as Record<string, unknown>;
  const c = (promo.conditions ?? {}) as Record<string, unknown>;
  const mult = promo.turnoverMultiplier ? Number(promo.turnoverMultiplier) : 5;
  const multBy = (minor: bigint) => wageringTargetMinor(minor, mult);
  const money = formatLedger;
  const out: string[] = [];

  switch (promo.kind) {
    case 'welcome': {
      const amt = toBig(r['amount']);
      if (amt != null) {
        out.push(t('card.welcome.info.benefit', { amount: money(amt) }));
        out.push(t('card.welcome.info.unlock', { mult, target: money(multBy(amt)) }));
      }
      out.push(t('card.welcome.info.terms'));
      break;
    }
    case 'first_deposit':
    case 'reload': {
      const pct = r['pct'] != null ? Number(r['pct']) : null;
      const cap = toBig(r['cap']);
      const minDep = toBig(c['minDeposit']);
      if (pct != null) {
        out.push(t(`card.${promo.kind}.info.benefit`, { pct, cap: cap != null ? money(cap) : '' }));
      }
      out.push(t(`card.${promo.kind}.info.unlock`, { mult }));
      if (promo.kind === 'first_deposit' && pct != null && minDep != null) {
        const exDep = minDep * 5n;
        let exBonus = (exDep * BigInt(Math.round(pct))) / 100n;
        if (cap != null && exBonus > cap) exBonus = cap;
        out.push(
          t('card.first_deposit.info.example', {
            deposit: money(exDep),
            bonus: money(exBonus),
            target: money(multBy(exBonus)),
          }),
        );
      }
      if (minDep != null) out.push(t(`card.${promo.kind}.info.terms`, { minDeposit: money(minDep) }));
      break;
    }
    case 'cashback':
    case 'turnover_rebate': {
      const kind = promo.kind;
      const rates = rebateRateConfig(promo.rewardRules);
      if (rates.defaultBips > 0) {
        out.push(t(`card.${kind}.info.rate`, { pct: percentOf(rates.defaultBips) }));
      }
      if (kind === 'turnover_rebate') {
        // Only the turnover engine reads per-type rates; the net-loss one
        // prices a whole period at `defaultBips`, so the same line on a
        // cashback card would promise a rate nothing ever applies.
        const perType = gameTypeRates(rates.gameTypeBips, t);
        if (perType) out.push(t('card.turnover_rebate.info.rateByGameType', { rates: perType }));
        // Last of the rate lines, because it is the one that qualifies the two
        // above it. The providers are not named: `providerBips` is keyed by
        // provider UUID and the promo payload carries no names to resolve them
        // against, so listing the ids would be worse than the disclosure.
        if (hasProviderOverrides(rates.providerBips)) {
          out.push(t('card.turnover_rebate.info.providerRatesVary'));
        }
      }
      if (rates.capMinor > 0n) {
        out.push(t('card.rebate.info.cap', { amount: money(rates.capMinor) }));
      }
      const period = rebatePeriodMode(kind, c);
      const anchor = rebatePeriodAnchor(c);
      out.push(
        t(`card.${kind}.info.period.${period}`, {
          weekday: t(`weekday.${anchor.dow}`),
          day: anchor.dom,
        }),
      );
      const claimDays = rebateClaimWindowDays(period, c);
      out.push(
        t(
          rebateClaimWindowEndsWithNextPeriod(period, claimDays)
            ? 'card.rebate.info.claimWindowUntilNextPeriod'
            : 'card.rebate.info.claimWindow',
          { days: claimDays },
        ),
      );
      const wagering = rebateMultiplier(promo.turnoverMultiplier);
      if (wagering != null) {
        out.push(t('card.rebate.info.turnover', { mult: wagering }));
        const expiry = rebateExpiryDays(promo.expiryDays);
        if (expiry != null) out.push(t('card.rebate.info.expiry', { days: expiry }));
      }
      break;
    }
    case 'daily_checkin': {
      const amounts = Array.isArray(r['amounts']) ? (r['amounts'] as unknown[]) : null;
      if (amounts && amounts.length === 7) {
        const lo = toBig(amounts[0]);
        const hi = toBig(amounts[6]);
        if (lo != null && hi != null) {
          out.push(t('card.daily_checkin.info.benefit', { lo: money(lo), hi: money(hi) }));
        }
      }
      out.push(t('card.daily_checkin.info.how'));
      break;
    }
  }
  return out.filter(Boolean);
}
